import React, { useState, useEffect, useCallback } from "react";
import {
  CiSearch,
  CiRedo,
  CiCircleCheck,
  CiFileOn,
  CiMoneyBill,
  CiInboxIn,
} from "react-icons/ci";
import PageTitle from "../../components/common/PageTitle";
import { useLocation, useNavigate } from "react-router-dom";
import ButtonTextIcon from "../../components/ui/ButtonTextIcon";
import {
  buildQueryParams,
  formatAmountValue,
  formatDateTime,
  decodeRolesFromToken,
} from "../../utilities/helpers";
import {
  API_URL,
  APP_CURRENCY,
  DEFAULT_PER_PAGE,
} from "../../utilities/constants";
import { useUI } from "../../context/ui";
import DatePicker from "react-datepicker";
import Pagination from "../../components/common/Pagination";
import FullPageLoader from "../../components/ui/FullPageLoader";
import ResultsPerPage from "../_common/ResultsPerPage";
import RSModal from "../../components/ui/RSModal";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import ButtonLightTextIcon from "../../components/ui/ButtonLightTextIcon";
import ButtonTextOnly from "../../components/ui/ButtonTextOnly";
import LoaderIcon from "../../components/common/LoaderIcon";
import "react-datepicker/dist/react-datepicker.css";
import Label from "../../components/ui/Label";
import useQueryParams from "../../hooks/useQueryParams";

const calculateTransactionStats = (txnIds) => {
  if (txnIds.length === 0) {
    return { amount: 0, count: 0 };
  }
  const totalAmount = txnIds.reduce(
    (total, txn) => total + (txn.transactionAmount || 0),
    0
  );
  return { amount: totalAmount, count: txnIds.length };
};

function getDefaultDateTimes() {
  const now = new Date();
  const startDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    6,
    0,
    0
  );
  let endDate;
  if (now.getDay() === 1) {
    const daysToSubtract = 2;
    endDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() - daysToSubtract,
      6,
      0,
      0
    );
  } else {
    endDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() - 1,
      6,
      0,
      0
    );
  }
  return {
    startDate: formatDateToCustomString(startDate),
    endDate: formatDateToCustomString(endDate),
  };
}

const formatDateToCustomString = (date) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}`;
};

const parseDateString = (dateString) => {
  const [datePart, hourPart] = dateString.split(" ");
  const [year, month, day] = datePart
    .split("-")
    .map((num) => parseInt(num, 10));
  const hour = parseInt(hourPart, 10);
  return new Date(year, month - 1, day, hour);
};

const DateTimePickerField = ({ field, form, ...props }) => {
  const { setFieldValue } = form;
  const handleChange = (date) => {
    if (date) {
      setFieldValue(field.name, formatDateToCustomString(date));
    } else {
      setFieldValue(field.name, "");
    }
  };
  const selectedDate = field.value ? parseDateString(field.value) : null;
  return (
    <DatePicker
      {...field}
      {...props}
      selected={selectedDate}
      onChange={handleChange}
      showTimeSelect
      timeIntervals={60}
      dateFormat="yyyy-MM-dd HH"
      placeholderText={props.placeholder}
    />
  );
};

const TAB_NORMAL = "normal";
const TAB_REWARD = "reward";

const Unsettled = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setSegmentTitle } = useUI();

  // Tab state
  const [activeTab, setActiveTab] = useState(TAB_NORMAL);

  // Shared state
  const [userRole, setUserRole] = useState(null);
  const [userClientId, setUserClientId] = useState("");
  const [clientIdValue, setClientIdValue] = useState("");
  const [clientData, setClientData] = useState(null);
  const [gasStationsData, setGasStationsData] = useState(null);
  const [loadingClient, setLoadingClient] = useState(false);
  const [loadingSite, setLoadingSite] = useState(false);

  // Normal tab state
  const [dataNormal, setDataNormal] = useState(null);
  const [loadingNormal, setLoadingNormal] = useState(false);

  // Reward tab state
  const [dataReward, setDataReward] = useState(null);
  const [loadingReward, setLoadingReward] = useState(false);

  // Select/checkbox logic
  const [allChecked, setAllChecked] = useState(true);
  const [excludedTxnIds, setExcludedTxnIds] = useState([]);
  const [includedTxnIds, setIncludedTxnIds] = useState([]);

  // Filters

  const { queryParams, updateQueryParam } = useQueryParams();
  const { startDate: defaultStartDate, endDate: defaultEndDate } =
    getDefaultDateTimes();
  const pageNo = queryParams.get("pageNo") || 0;
  const pageSize = queryParams.get("pageSize") || DEFAULT_PER_PAGE;
  const fromDateTime = queryParams.get("fromDateTime") || defaultEndDate;
  const toDateTime = queryParams.get("toDateTime") || defaultStartDate;
  const gasStationId = queryParams.get("gasStationId") || "";
  const clientId = queryParams.get("clientId") || "";

  // Role and clientId extraction logic
  useEffect(() => {
    const roles = decodeRolesFromToken();
    if (roles && roles.length > 0) {
      setUserRole(roles[0]);
    }
    // Fetch user profile to get clientId
    fetch(API_URL + "/api/1.0.0/user/profile", {
      headers: {
        "Content-Type": "application/json",
        "X-JWT-Assertion": localStorage.getItem("token"),
      },
    })
      .then((res) => res.json())
      .then((profile) => {
        setUserClientId(profile?.data?.clientId || "");
        // If role is 6, 8, 3, set clientIdValue automatically
        if ([6, 8, 3].includes(roles[0]) && profile?.data?.clientId) {
          setClientIdValue(profile.data.clientId);
        }
      });
  }, []);

  // Fetch clients
  const fetchData = useCallback(async (endpoint, setState, setLoading) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: {
          "Content-Type": "application/json",
          "X-JWT-Assertion": localStorage.getItem("token"),
        },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.result.responseCode === 200) {
          setState(data.data || null);
        } else {
          setState(null);
        }
      } else {
        setState(null);
      }
    } catch (e) {
      console.error("Error fetching data", e);
      setState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch all clients on mount
  useEffect(() => {
    fetchData(
      "/api/1.0.0/client?pageNo=0&pageSize=100&sortOrder=desc",
      setClientData,
      setLoadingClient
    );
  }, [fetchData]);

  // --- Fetch sites when clientIdValue changes ---
  useEffect(() => {
    const fetchId = [6, 8, 3].includes(userRole) ? userClientId : clientIdValue;
    if (fetchId) {
      fetchData(
        `/api/1.0.0/gasstation?pageNo=0&pageSize=100&sortOrder=desc&clientID=${fetchId}`,
        setGasStationsData,
        setLoadingSite
      );
    }
  }, [clientIdValue, userClientId, userRole, fetchData]);

  // Fetch normal transactions
  const callApiNormal = useCallback(() => {
    const effectiveClientId = [6, 8, 3].includes(userRole)
      ? userClientId
      : clientId;
    if (fromDateTime && toDateTime && effectiveClientId && gasStationId) {
      const queryParams = buildQueryParams({
        sortOrder: "desc",
        endDateTime: toDateTime.replace(" ", "T") + ":00:00",
        startDateTime: fromDateTime.replace(" ", "T") + ":00:00",
        gasStationId,
        pageNo,
        pageSize,
        clientId: effectiveClientId,
      });
      setClientIdValue(effectiveClientId);
      fetchData(
        `/api/1.0.0/settlement?${queryParams}`,
        setDataNormal,
        setLoadingNormal
      );
    } else {
      setDataNormal(null);
    }
  }, [
    pageNo,
    pageSize,
    fromDateTime,
    toDateTime,
    clientId,
    gasStationId,
    userRole,
    userClientId,
    fetchData,
  ]);

  // Fetch reward transactions
  const callApiReward = useCallback(() => {
    const effectiveClientId = [6, 8, 3].includes(userRole)
      ? userClientId
      : clientId;
    if (fromDateTime && toDateTime && effectiveClientId && gasStationId) {
      const queryParams = buildQueryParams({
        sortOrder: "desc",
        endDateTime: toDateTime.replace(" ", "T") + ":00:00",
        startDateTime: fromDateTime.replace(" ", "T") + ":00:00",
        gasStationId,
        pageNo,
        pageSize,
        clientId: effectiveClientId,
      });
      setClientIdValue(effectiveClientId);
      fetchData(
        `/api/1.0.0/settlement/unsettled-reward?${queryParams}`,
        setDataReward,
        setLoadingReward
      );
    } else {
      setDataReward(null);
    }
  }, [
    pageNo,
    pageSize,
    fromDateTime,
    toDateTime,
    clientId,
    gasStationId,
    userRole,
    userClientId,
    fetchData,
  ]);

  // Call the correct API when filters or tab change
  useEffect(() => {
    // Reset page no 0 and remove filter and search params
    if (activeTab === TAB_NORMAL) {
      callApiNormal();
      setSegmentTitle("Unsettled Transaction");
    } else {
      callApiReward();
      setSegmentTitle("Reward Unsettled Transaction");
    }
  }, [
    location.search,
    userRole,
    userClientId,
    activeTab,
    callApiNormal,
    callApiReward,
    setSegmentTitle,
  ]);

  // Checkbox logic
  const toggleTxnId = (d) => {
    if (allChecked) {
      setExcludedTxnIds((prevIds) => {
        const itemExists = prevIds.find((obj) => obj.id === d.id);
        if (itemExists) {
          return prevIds.filter((obj) => obj.id !== d.id);
        } else {
          return [...prevIds, { ...d }];
        }
      });
    } else {
      setIncludedTxnIds((prevIds) => {
        const itemExists = prevIds.find((obj) => obj.id === d.id);
        if (itemExists) {
          return prevIds.filter((obj) => obj.id !== d.id);
        } else {
          return [...prevIds, { ...d }];
        }
      });
    }
  };
  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setAllChecked(isChecked);
    setIncludedTxnIds([]);
    setExcludedTxnIds([]);
  };

  // Formik validation
  const isValidDateFormat = (value) => {
    const regex = /^\d{4}-\d{2}-\d{2} \d{2}$/;
    if (!regex.test(value)) return false;
    const date = new Date(value.replace(" ", "T") + ":00");
    return date.toString() !== "Invalid Date";
  };

  const FormValidation = Yup.object().shape({
    clientId: Yup.string().required("Client is required"),
    gasStationId: Yup.string().required("Site is required"),
    fromDateTime: Yup.string()
      .required("Start date time is required")
      .test(
        "valid-date-format",
        "Invalid start date time format. Use yyyy-MM-dd HH",
        (value) => isValidDateFormat(value)
      )
      .test(
        "before-to-date",
        "Start date time must be before End date time",
        function (value) {
          const { toDateTime } = this.parent;
          if (!value || !toDateTime) return true;
          const toDateString =
            typeof toDateTime === "string" ? toDateTime : String(toDateTime);
          const fromDate = new Date(value.replace(" ", "T") + ":00");
          const toDate = new Date(toDateString.replace(" ", "T") + ":00");
          return fromDate < toDate;
        }
      ),
    toDateTime: Yup.string()
      .required("End date time is required")
      .test(
        "valid-date-format",
        "Invalid end date time format. Use yyyy-MM-dd HH",
        (value) => isValidDateFormat(value)
      )
      .test(
        "after-from-date",
        "End date time must be after Start date time",
        function (value) {
          const { fromDateTime } = this.parent;
          if (!value || !fromDateTime) return true;
          const fromDateString =
            typeof fromDateTime === "string"
              ? fromDateTime
              : String(fromDateTime);
          const fromDate = new Date(fromDateString.replace(" ", "T") + ":00");
          const toDate = new Date(value.replace(" ", "T") + ":00");
          return fromDate < toDate;
        }
      )
      .test(
        "before-2-hours",
        "End date time must be at least 2 hours before the current time",
        function (value) {
          const now = new Date();
          const endDateTime = new Date(value.replace(" ", "T") + ":00");
          return endDateTime <= new Date(now.getTime() - 2 * 60 * 60 * 1000);
        }
      ),
  });

  const initialValues = {
    fromDateTime: fromDateTime,
    toDateTime: toDateTime,
    clientId: [6, 8, 3].includes(userRole) ? userClientId : clientId,
    gasStationId: gasStationId,
  };

  const resetFilter = () => {
    navigate(`${"/unsettled"}?pageNo=0&pageSize=${DEFAULT_PER_PAGE}`);
  };

  const handleClientChange = async (event) => {
    const clientIdValue = event.target.value;
    setClientIdValue(clientIdValue);
  };

  // Transaction stats
  const { amount: excludedTxnAmount, count: excludedTxnNo } =
    calculateTransactionStats(excludedTxnIds);
  const { amount: includedTxnAmount, count: includedTxnNo } =
    calculateTransactionStats(includedTxnIds);

  // Render
  return (
    <section>
      <PageTitle title="Transactions" />
      <FullPageLoader
        isActive={
          loadingNormal || loadingReward || loadingClient || loadingSite
        }
      />

      {/* Filters */}
      <div className="flex center-left responsive-container">
        <div>
          <Formik
            initialValues={initialValues}
            validationSchema={FormValidation}
            enableReinitialize
            onSubmit={async (values, { resetForm }) => {
              const queryParams = buildQueryParams({
                clientId: [6, 8, 3].includes(userRole)
                  ? userClientId
                  : values.clientId,
                fromDateTime: values.fromDateTime,
                gasStationId: values.gasStationId,
                toDateTime: values.toDateTime,
                pageNo: 0,
                pageSize,
              });
              navigate(`${"/unsettled"}?${queryParams}`);
            }}
          >
            {({ values, errors, touched, setFieldValue, resetForm }) => (
              <Form>
                <div
                  className="flex center-left responsive-container"
                  style={{
                    padding: 0,
                    margin: 0,
                  }}
                >
                  <div>
                    <Label title={"Filters:"} />
                    <ResultsPerPage
                      targetURL={`${"/unsettled"}?${buildQueryParams({
                        fromDateTime,
                        toDateTime,
                        clientId: [6, 8, 3].includes(userRole)
                          ? userClientId
                          : clientId,
                        gasStationId,
                      })}&pageNo=0&pageSize=`}
                    />
                  </div>
                  {![6, 8, 3].includes(userRole) && (
                    <div>
                      <Label title={"Client:"} />
                      <Field
                        as="select"
                        name="clientId"
                        className={
                          "responsive-select " +
                          (errors.clientId && touched.clientId ? " error" : "")
                        }
                        onClick={handleClientChange}
                      >
                        <option value="">Select Client</option>
                        {clientData &&
                          clientData?.clients &&
                          clientData.clients?.map((c, i) => (
                            <option value={c.id} key={i}>
                              {" "}
                              {c.name}{" "}
                            </option>
                          ))}
                      </Field>
                    </div>
                  )}
                  <div>
                    <Label title={"Site:"} />
                    <Field
                      as="select"
                      name="gasStationId"
                      className={
                        "responsive-select " +
                        (errors.gasStationId && touched.gasStationId
                          ? " error"
                          : "")
                      }
                    >
                      <option value="">Select Site</option>
                      {gasStationsData &&
                        gasStationsData?.gasStations &&
                        gasStationsData.gasStations?.map((c, i) => (
                          <option value={c.id} key={i}>
                            {" "}
                            {c.name}{" "}
                          </option>
                        ))}
                    </Field>
                  </div>
                  <div>
                    <Label title={"From Date Time:"} />
                    <Field
                      name="fromDateTime"
                      component={DateTimePickerField}
                      value={values.fromDateTime}
                      placeholder="Select From Date Time"
                      className={
                        " " +
                        (errors.fromDateTime && touched.fromDateTime
                          ? " error"
                          : "")
                      }
                    />
                  </div>
                  <div>
                    <Label title={"To Date Time:"} />
                    <Field
                      name="toDateTime"
                      component={DateTimePickerField}
                      value={values.toDateTime}
                      placeholder="Select To Date Time"
                      className={
                        " " +
                        (errors.toDateTime && touched.toDateTime
                          ? " error"
                          : "")
                      }
                    />
                  </div>
                  <div>
                    <p>&nbsp;</p>
                    <ButtonTextIcon type="submit" icon={<CiSearch />} />
                  </div>
                  <div className="responsive-button">
                    <p>&nbsp;</p>
                    <ButtonTextIcon
                      type="button"
                      onClick={resetFilter}
                      icon={<CiRedo />}
                    />
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <div
        className="rs-tabs flex"
        style={{
          gap: "15px",
          margin: "10px 20px",
          justifyContent: "space-between",
        }}
      >
        <ul className="tab-selector">
          <li
            className={activeTab===TAB_NORMAL ? "active" : ""}
           onClick={() => {
            updateQueryParam("pageNo", 0);
            setActiveTab(TAB_NORMAL);
          }}
            style={{ cursor: "pointer" }}
          >
            <h6>Fuel Transactions</h6>
          </li>
          <li
            className={activeTab === TAB_REWARD ? "active" : ""}
            onClick={() => {
            updateQueryParam("pageNo", 0);
            setActiveTab(TAB_REWARD);
          }}
            style={{ cursor: "pointer" }}
          >
            <h6>Rewards Transactions</h6>
          </li>
        </ul>
      </div>
      {/* Tab content */}
      {activeTab === TAB_NORMAL && (
        <>
          {dataNormal && dataNormal.transactionDetails && (
            <div className="dashboard" style={{ padding: "0", margin: 0 }}>
              <div className="flex center-left responsive-container">
                <div className="quick-links" style={{ width: "85%" }}>
                  <div className="one flex center-left">
                    <div className="flex center-left" style={{ gap: "10px" }}>
                      <CiMoneyBill
                        className="color-secondary"
                        style={{ height: "40px", width: "auto" }}
                      />
                      <div>
                        <p className="color-faded font-size-12 f-w-400 l-h-1">
                          Total Settlement Amount
                        </p>
                        <h6 className="color-primary f-w-700 l-h-1">
                          {APP_CURRENCY}{" "}
                          {allChecked
                            ? dataNormal?.transactionDetails
                                ?.totalCreditAmount != null
                              ? `${formatAmountValue(
                                  dataNormal.transactionDetails
                                    .totalCreditAmount - excludedTxnAmount
                                )}/- `
                              : `0 /-`
                            : dataNormal?.transactionDetails
                                ?.totalCreditAmount != null
                            ? `${formatAmountValue(includedTxnAmount)}/- `
                            : `0 /-`}
                        </h6>
                      </div>
                    </div>
                  </div>
                  <div className="one flex center-left">
                    <div className="flex center-left" style={{ gap: "10px" }}>
                      <CiFileOn
                        className="color-secondary"
                        style={{ height: "40px", width: "auto" }}
                      />
                      <div>
                        <p className="color-faded font-size-12 f-w-400 l-h-1">
                          Total Settlement Transaction
                        </p>
                        <h6 className="color-primary f-w-700 l-h-1">
                          {allChecked
                            ? dataNormal?.transactionDetails
                                ?.totalTransaction != null
                              ? dataNormal.transactionDetails.totalTransaction -
                                excludedTxnNo
                              : 0
                            : dataNormal?.transactionDetails
                                ?.totalTransaction != null
                            ? includedTxnNo
                            : 0}{" "}
                          Transactions
                        </h6>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="results">
            <div className="rs-table-holder">
              <table className="rs-table">
                <thead>
                  <tr>
                    <th className="flex center-center">#</th>
                    <th>Associated acc.</th>
                    <th>Account no.</th>
                    <th>Amount</th>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {dataNormal?.transactionHistoryResponses?.map((d, i) => {
                    const formattedDateTime = formatDateTime(d.transactionTime);
                    return (
                      <tr key={i}>
                        <td>{pageNo * pageSize + 1 + i}</td>
                        <td>{d.associateAccount || "-"}</td>
                        <td>{d.account || "-"}</td>
                        <td>
                          {APP_CURRENCY}{" "}
                          {formatAmountValue(d.transactionAmount)}
                          /-
                        </td>
                        <td>{formattedDateTime}</td>
                        <td>{d.description}</td>
                        <td>
                          <div className="flex center-center">
                            {d.transactionReceipt && (
                              <div>
                                <a href={d.transactionReceipt} target="_blank">
                                  <CiInboxIn />
                                </a>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {(dataNormal?.transactionHistoryResponses?.length === 0 ||
                    !dataNormal) &&
                    !loadingNormal && (
                      <tr>
                        <td
                          colSpan={7}
                          style={{ textAlign: "center", padding: "10px" }}
                        >
                          <p> No records found. </p>
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>
          </div>
          <br />
          {dataNormal && (
            <Pagination
              totalRecords={dataNormal.totalRecords}
              pageNo={dataNormal.pageNo}
              pageSize={dataNormal.pageSize}
              baseUrl={`/unsettled?${buildQueryParams({
                toDateTime,
                fromDateTime,
                clientId: [6, 8, 3].includes(userRole)
                  ? userClientId
                  : clientId,
                gasStationId,
              })}&`}
            />
          )}
        </>
      )}

      {activeTab === TAB_REWARD && (
        <>
          {dataReward && dataReward.transactionDetails && (
            <div className="dashboard" style={{ padding: "0", margin: 0 }}>
              <div className="flex center-left responsive-container">
                <div className="quick-links" style={{ width: "85%" }}>
                  <div className="one flex center-left">
                    <div className="flex center-left" style={{ gap: "10px" }}>
                      <CiMoneyBill
                        className="color-secondary"
                        style={{ height: "40px", width: "auto" }}
                      />
                      <div>
                        <p className="color-faded font-size-12 f-w-400 l-h-1">
                          Total Settlement Amount
                        </p>
                        <h6 className="color-primary f-w-700 l-h-1">
                          {APP_CURRENCY}{" "}
                          {allChecked
                            ? dataReward?.transactionDetails
                                ?.totalRewardCreditAmount != null
                              ? `${formatAmountValue(
                                  dataReward.transactionDetails
                                    .totalRewardCreditAmount - excludedTxnAmount
                                )}/- `
                              : `0 /-`
                            : dataReward?.transactionDetails
                                ?.totalRewardCreditAmount != null
                            ? `${formatAmountValue(includedTxnAmount)}/- `
                            : `0 /-`}
                        </h6>
                      </div>
                    </div>
                  </div>
                  <div className="one flex center-left">
                    <div className="flex center-left" style={{ gap: "10px" }}>
                      <CiFileOn
                        className="color-secondary"
                        style={{ height: "40px", width: "auto" }}
                      />
                      <div>
                        <p className="color-faded font-size-12 f-w-400 l-h-1">
                          Total Settlement Transaction
                        </p>
                        <h6 className="color-primary f-w-700 l-h-1">
                          {allChecked
                            ? dataReward?.transactionDetails
                                ?.totalTransaction != null
                              ? dataReward.transactionDetails.totalTransaction -
                                excludedTxnNo
                              : 0
                            : dataReward?.transactionDetails
                                ?.totalTransaction != null
                            ? includedTxnNo
                            : 0}{" "}
                          Transactions
                        </h6>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="results">
            <div className="rs-table-holder">
              <table className="rs-table">
                <thead>
                  <tr>
                    <th className="flex center-center">#</th>
                    <th>Associated acc.</th>
                    <th>Account no.</th>
                    <th>Amount</th>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {dataReward?.transactionHistoryResponses?.map((d, i) => {
                    const formattedDateTime = formatDateTime(d.transactionTime);
                    return (
                      <tr key={i}>
                        <td>{pageNo * pageSize + 1 + i}</td>
                        <td>{d.associateAccount || "-"}</td>
                        <td>{d.account || "-"}</td>
                        <td>
                          {APP_CURRENCY}{" "}
                          {formatAmountValue(d.transactionAmount)}
                          /-
                        </td>
                        <td>{formattedDateTime}</td>
                        <td>{d.description}</td>
                        <td>
                          <div className="flex center-center">
                            {d.transactionReceipt && (
                              <div>
                                <a href={d.transactionReceipt} target="_blank">
                                  <CiInboxIn />
                                </a>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {(dataReward?.transactionHistoryResponses?.length === 0 ||
                    !dataReward) &&
                    !loadingReward && (
                      <tr>
                        <td
                          colSpan={7}
                          style={{ textAlign: "center", padding: "10px" }}
                        >
                          <p> No records found. </p>
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>
          </div>
          <br />
          {dataReward && (
            <Pagination
              totalRecords={dataReward.totalRecords}
              pageNo={dataReward.pageNo}
              pageSize={dataReward.pageSize}
              baseUrl={`/unsettled?${buildQueryParams({
                toDateTime,
                fromDateTime,
                clientId: [6, 8, 3].includes(userRole)
                  ? userClientId
                  : clientId,
                gasStationId,
              })}&`}
            />
          )}
        </>
      )}
    </section>
  );
};

export default Unsettled;
